import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Plus, Trash2, Clock, CheckCircle2, Circle, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CalendarView({ token, beings, wants, todos, onRefreshData }) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  const [anchors, setAnchors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlotTime, setSelectedSlotTime] = useState(null);
  const [editingAnchor, setEditingAnchor] = useState(null);
  const [hourVal, setHourVal] = useState('09');
  const [minVal, setMinVal] = useState('00');
  const [duration, setDuration] = useState(60);
  const [customDurationVal, setCustomDurationVal] = useState(120);
  const [durationUnit, setDurationUnit] = useState('mins');

  const getPreviousDateStr = (dateStr) => {
    if (!dateStr) return '';
    const [yyyy, mm, dd] = dateStr.split('-').map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    d.setDate(d.getDate() - 1);
    const prevY = d.getFullYear();
    const prevM = String(d.getMonth() + 1).padStart(2, '0');
    const prevD = String(d.getDate()).padStart(2, '0');
    return `${prevY}-${prevM}-${prevD}`;
  };

  const fetchAnchors = useCallback(async () => {
    setLoading(true);
    try {
      const configCurr = {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate }
      };
      
      const prevDate = getPreviousDateStr(selectedDate);
      const configPrev = {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: prevDate }
      };

      const [resCurr, resPrev] = await Promise.all([
        axios.get(`${API}/calendar/anchors`, configCurr),
        axios.get(`${API}/calendar/anchors`, configPrev)
      ]);

      const currData = (resCurr.data || []).map(a => ({ ...a, originDate: selectedDate }));
      const prevData = (resPrev.data || []).map(a => ({ ...a, originDate: prevDate }));

      const activePrevData = prevData.filter(a => {
        if (!a.time || typeof a.duration === 'undefined') return false;
        const [h, m] = a.time.split(':').map(Number);
        const startMins = h * 60 + m;
        const endMins = startMins + parseInt(a.duration, 10);
        return endMins > 1440;
      });

      setAnchors([...activePrevData, ...currData]);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load scheduled items');
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate]);

  useEffect(() => {
    fetchAnchors();
  }, [fetchAnchors]);

  useEffect(() => {
    if (selectedSlotTime) {
      const [h, m] = selectedSlotTime.split(':');
      setHourVal(h);
      setMinVal(m || '00');
      setDuration(60);
      setCustomDurationVal(120);
      setDurationUnit('mins');
    }
  }, [selectedSlotTime]);

  useEffect(() => {
    if (editingAnchor) {
      const [h, m] = editingAnchor.time.split(':');
      setHourVal(h);
      setMinVal(m || '00');
      
      const dur = editingAnchor.duration || 60;
      if ([30, 60, 90, 120].includes(dur)) {
        setDuration(dur);
      } else {
        setDuration('custom');
        if (dur % 60 === 0) {
          setCustomDurationVal(dur / 60);
          setDurationUnit('hours');
        } else {
          setCustomDurationVal(dur);
          setDurationUnit('mins');
        }
      }
    }
  }, [editingAnchor]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedSlotTime(null);
        setEditingAnchor(null);
      }
    };
    if (selectedSlotTime || editingAnchor) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedSlotTime, editingAnchor]);

  useEffect(() => {
    if (!selectedSlotTime && !editingAnchor) return;

    const modalElement = document.getElementById('schedule-picker-modal');
    if (!modalElement) return;

    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusables = Array.from(modalElement.querySelectorAll(focusableSelector))
        .filter(el => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');

      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusables[0];
      const lastElement = focusables[focusables.length - 1];

      if (!focusables.includes(document.activeElement)) {
        firstElement.focus();
        e.preventDefault();
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    // Auto-focus the first element
    const focusables = Array.from(modalElement.querySelectorAll(focusableSelector))
      .filter(el => !el.hasAttribute('disabled'));
    if (focusables.length > 0) {
      focusables[0].focus();
    }

    window.addEventListener('keydown', handleTabKey);
    return () => {
      window.removeEventListener('keydown', handleTabKey);
    };
  }, [selectedSlotTime, editingAnchor]);

  // Navigate date
  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    setSelectedDate(`${yyyy}-${mm}-${dd}`);
  };

  // Identify unscheduled items (those not yet placed on this date)
  const scheduledItemIds = new Set(anchors.map(a => a.item_id));
  const unscheduledBeings = beings.filter(b => !scheduledItemIds.has(b.id));
  const unscheduledWants = wants.filter(w => !w.completed && !scheduledItemIds.has(w.id));
  const unscheduledTodos = todos.filter(t => !t.completed && !scheduledItemIds.has(t.id));

  // Schedule an item
  const handleSchedule = async (itemType, itemId) => {
    if (!selectedSlotTime) return;
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const combinedTime = `${hourVal}:${minVal}`;
      const finalDuration = duration === 'custom'
        ? (durationUnit === 'hours' ? customDurationVal * 60 : customDurationVal)
        : parseInt(duration, 10);

      await axios.post(`${API}/calendar/anchors`, {
        item_type: itemType,
        item_id: itemId,
        date: selectedDate,
        time: combinedTime,
        duration: finalDuration
      }, config);
      toast.success('Scheduled successfully');
      setSelectedSlotTime(null);
      fetchAnchors();
      if (onRefreshData) onRefreshData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to schedule item');
    }
  };

  // Update scheduled item time/duration
  const handleUpdateSchedule = async () => {
    if (!editingAnchor) return;
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const combinedTime = `${hourVal}:${minVal}`;
      const finalDuration = duration === 'custom'
        ? (durationUnit === 'hours' ? customDurationVal * 60 : customDurationVal)
        : parseInt(duration, 10);

      await axios.put(`${API}/calendar/anchors/${editingAnchor.id}`, {
        time: combinedTime,
        duration: finalDuration
      }, config);
      toast.success('Schedule updated successfully');
      setEditingAnchor(null);
      fetchAnchors();
      if (onRefreshData) onRefreshData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update schedule');
    }
  };

  // Unschedule/delete anchor
  const handleUnschedule = async (anchorId) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/calendar/anchors/${anchorId}`, config);
      toast.success('Unscheduled successfully');
      fetchAnchors();
      if (onRefreshData) onRefreshData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to unschedule item');
    }
  };

  // Hours array from 07:00 to 22:00
  const hours = Array.from({ length: 16 }, (_, i) => {
    const hr = 7 + i;
    return String(hr).padStart(2, '0') + ':00';
  });

  // For a slot time "09:00", return the list of segments (either anchors or gaps)
  const getSlotSegments = (time) => {
    const [sh, sm] = time.split(':').map(Number);
    const slotStartMins = sh * 60 + sm;
    const slotEndMins = slotStartMins + 60;

    const getNormalizedTimes = (a) => {
      if (!a.time) return { start: 0, end: 0 };
      const [ah, am] = a.time.split(':').map(Number);
      const start = ah * 60 + am;
      const end = start + a.duration;
      if (a.originDate !== selectedDate) {
        return { start: start - 1440, end: end - 1440 };
      }
      return { start, end };
    };

    const overlapping = anchors.filter(a => {
      if (!a.time) return false;
      const { start, end } = getNormalizedTimes(a);
      return start < slotEndMins && end > slotStartMins;
    });

    overlapping.sort((a, b) => {
      const tA = getNormalizedTimes(a);
      const tB = getNormalizedTimes(b);
      return tA.start - tB.start;
    });

    const segments = [];
    let currentMins = slotStartMins;

    overlapping.forEach(a => {
      const { start: startMins, end: endMins } = getNormalizedTimes(a);

      if (startMins > currentMins) {
        const gapStartH = Math.floor(currentMins / 60);
        const gapStartM = currentMins % 60;
        const gapTimeStr = `${String(gapStartH).padStart(2, '0')}:${String(gapStartM).padStart(2, '0')}`;
        segments.push({
          type: 'gap',
          time: gapTimeStr,
          duration: startMins - currentMins
        });
      }

      const isContinuation = startMins < slotStartMins;
      segments.push({
        type: 'anchor',
        anchor: a,
        isContinuation,
        time: isContinuation ? time : a.time
      });

      currentMins = Math.min(slotEndMins, endMins);
    });

    if (currentMins < slotEndMins) {
      const gapStartH = Math.floor(currentMins / 60);
      const gapStartM = currentMins % 60;
      const gapTimeStr = `${String(gapStartH).padStart(2, '0')}:${String(gapStartM).padStart(2, '0')}`;
      segments.push({
        type: 'gap',
        time: gapTimeStr,
        duration: slotEndMins - currentMins
      });
    }

    return segments;
  };

  // Helper to calculate end time based on duration
  const getEndTime = (startTime, durationMins) => {
    if (!startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    const totalMins = h * 60 + m + parseInt(durationMins, 10);
    const endH = Math.floor(totalMins / 60) % 24;
    const endM = totalMins % 60;
    return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  };

  // Helper to format duration in a human-friendly way (e.g. 10h, 1h 30m, 45m)
  const formatDuration = (mins) => {
    if (!mins) return '0m';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) {
      return `${h}h ${m}m`;
    } else if (h > 0) {
      return `${h}h`;
    }
    return `${m}m`;
  };

  // Readable Date Display
  const getReadableDate = () => {
    const d = new Date(selectedDate);
    return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="calendar-view-container">
      {/* Calendar Header controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/50 pb-6 mb-6">
        <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-4 w-full sm:w-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => changeDate(-1)}
            data-testid="calendar-prev-day-btn"
            className="rounded-full flex-shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <CalendarIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-sans font-medium text-foreground truncate" data-testid="calendar-date-display">
              {getReadableDate()}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => changeDate(1)}
            data-testid="calendar-next-day-btn"
            className="rounded-full flex-shrink-0"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
        
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          data-testid="calendar-datepicker-input"
          className="w-full sm:w-auto bg-transparent border border-border/50 rounded-lg px-3 py-2 text-sm font-sans text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 text-center"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 lg:h-[calc(100vh-12rem)] lg:overflow-hidden h-auto">
        {/* Unscheduled Sidebar column (Left side) */}
        <div data-testid="calendar-unscheduled-sidebar" className="order-2 lg:order-1 lg:col-span-1 border border-border/50 bg-card/20 backdrop-blur-sm rounded-xl p-6 flex flex-col lg:h-full lg:overflow-hidden h-auto lg:max-h-full max-h-[60vh] overflow-y-auto">
          <h3 className="text-lg font-sans font-medium text-foreground border-b border-border/50 pb-3 mb-4">
            {t('calendar.unscheduledItems', 'Unscheduled Items')}
          </h3>
          
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Beings (Identities/Habits) */}
            <div>
              <h4 className="text-xs font-sans font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                {t('calendar.beings', 'Beings (Identities & Habits)')}
              </h4>
              {unscheduledBeings.length === 0 ? (
                <p className="text-sm font-sans text-muted-foreground/60 italic pl-1">{t('calendar.noneUnscheduled', 'None unscheduled')}</p>
              ) : (
                <div className="space-y-2">
                  {unscheduledBeings.map(b => (
                    <div 
                      key={b.id} 
                      className="bg-card/40 border border-border/30 rounded-lg p-3 flex justify-between items-center hover:bg-card/75 transition-colors"
                      data-testid={`unscheduled-being-${b.id}`}
                    >
                      <div>
                        <div className="text-sm font-sans text-foreground">{b.title}</div>
                        <div className="text-xs font-sans text-muted-foreground">{b.type}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedSlotTime(hours[0])} // default to first slot to choose
                        className="h-8 w-8 text-primary hover:text-accent-foreground"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Wants (Goals) */}
            <div>
              <h4 className="text-xs font-sans font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                {t('calendar.wants', 'Wants (Goals & Desires)')}
              </h4>
              {unscheduledWants.length === 0 ? (
                <p className="text-sm font-sans text-muted-foreground/60 italic pl-1">{t('calendar.noneUnscheduled', 'None unscheduled')}</p>
              ) : (
                <div className="space-y-2">
                  {unscheduledWants.map(w => (
                    <div 
                      key={w.id} 
                      className="bg-card/40 border border-border/30 rounded-lg p-3 flex justify-between items-center hover:bg-card/75 transition-colors"
                      data-testid={`unscheduled-want-${w.id}`}
                    >
                      <div className="text-sm font-sans text-foreground">{w.title}</div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedSlotTime(hours[0])}
                        className="h-8 w-8 text-primary hover:text-accent-foreground"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Todos (Tasks) */}
            <div>
              <h4 className="text-xs font-sans font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                {t('calendar.todos', "Todos (Today's Actions)")}
              </h4>
              {unscheduledTodos.length === 0 ? (
                <p className="text-sm font-sans text-muted-foreground/60 italic pl-1">{t('calendar.noneUnscheduled', 'None unscheduled')}</p>
              ) : (
                <div className="space-y-2">
                  {unscheduledTodos.map(t => (
                    <div 
                      key={t.id} 
                      className="bg-card/40 border border-border/30 rounded-lg p-3 flex justify-between items-center hover:bg-card/75 transition-colors"
                      data-testid={`unscheduled-todo-${t.id}`}
                    >
                      <div className="text-sm font-sans text-foreground flex items-center gap-2">
                        <Circle className="w-3.5 h-3.5 text-muted-foreground" />
                        {t.title}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedSlotTime(hours[0])}
                        className="h-8 w-8 text-primary hover:text-accent-foreground"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Vertical Timeline Grid column (Right side) */}
        <div className="order-1 lg:order-2 lg:col-span-2 border border-border/50 bg-card/20 backdrop-blur-sm rounded-xl p-6 flex flex-col lg:h-full lg:overflow-hidden h-auto">
          <h3 className="text-lg font-sans font-medium text-foreground border-b border-border/50 pb-3 mb-4">
            {t('calendar.title', 'Day Schedule')}
          </h3>
          
          <div className="flex-1 lg:overflow-y-auto space-y-4 pr-2 lg:max-h-full max-h-[70vh] overflow-y-auto" data-testid="calendar-timeline-scroll">
            {loading ? (
              <div className="h-full flex items-center justify-center text-muted-foreground italic">
                {t('calendar.loading', 'Loading schedule...')}
              </div>
            ) : (
              hours.map(time => {
                const segments = getSlotSegments(time);
                return (
                  <div key={time} className="flex gap-4 items-start py-2 group">
                    {/* Time Column */}
                    <div className="w-12 text-right text-xs font-sans text-muted-foreground font-semibold pt-4">
                      {time}
                    </div>
                    
                    {/* Event Box Slots Column */}
                    <div className="flex-1 flex flex-col gap-2">
                      {segments.map((segment, idx) => {
                        if (segment.type === 'anchor') {
                          if (segment.isContinuation) {
                            return (
                              <div 
                                key={idx}
                                className="border border-dashed border-border bg-muted/20 dark:bg-muted/5 rounded-lg p-3.5 flex justify-between items-center text-xs text-muted-foreground font-sans animate-in fade-in duration-200 cursor-pointer hover:bg-muted/30 hover:border-primary/40 transition-all"
                                data-testid={`continuation-slot-${time}`}
                                onClick={() => setEditingAnchor(segment.anchor)}
                              >
                                <div className="flex items-center gap-2">
                                  <Clock className="w-3.5 h-3.5 text-muted-foreground/60" />
                                  <span>
                                    {t('calendar.continued', 'Continued:')} <span className="font-semibold text-foreground/80">{segment.anchor.item_details?.title || 'Loading...'}</span>
                                    <span className="mx-1.5 text-muted-foreground/50">•</span>
                                    {t('calendar.until', { time: getEndTime(segment.anchor.time, segment.anchor.duration), defaultValue: '(until {{time}})' })}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnschedule(segment.anchor.id);
                                  }}
                                  data-testid={`unschedule-btn-${segment.anchor.id}`}
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            );
                          }
                          return (
                            <div 
                              key={idx}
                              className={`border rounded-lg p-3.5 flex justify-between items-center transition-all animate-in fade-in duration-200 cursor-pointer hover:border-primary/40 ${
                                segment.anchor.item_type === 'being' 
                                  ? 'bg-accent/10 border-accent/40 text-foreground' 
                                  : segment.anchor.item_type === 'want'
                                  ? 'bg-secondary/40 border-border text-foreground'
                                  : 'bg-card/80 border-border text-foreground'
                              }`}
                              data-testid={`scheduled-slot-${time}`}
                              onClick={() => setEditingAnchor(segment.anchor)}
                            >
                              <div className="flex items-center gap-3">
                                <Clock className="w-4 h-4 text-muted-foreground" />
                                <div>
                                  <div className="text-sm font-sans font-medium">
                                    {segment.anchor.item_details?.title || 'Loading...'}
                                  </div>
                                  <div className="text-[11px] text-muted-foreground font-sans">
                                    <span className="capitalize font-semibold">{t(`analytics.${segment.anchor.item_type}.label`, segment.anchor.item_type)}</span>
                                    <span className="mx-1.5">•</span>
                                    <span>{segment.anchor.time} - {getEndTime(segment.anchor.time, segment.anchor.duration)} ({formatDuration(segment.anchor.duration)})</span>
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnschedule(segment.anchor.id);
                                }}
                                data-testid={`unschedule-btn-${segment.anchor.id}`}
                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        } else {
                          return (
                            <button
                              key={idx}
                              onClick={() => setSelectedSlotTime(segment.time)}
                              data-testid={`empty-slot-${segment.time}`}
                              className="w-full border border-dashed border-border/50 hover:border-primary/50 rounded-lg p-3.5 text-left text-xs font-sans text-muted-foreground/60 hover:text-primary transition-all duration-200 cursor-pointer"
                            >
                              {t('calendar.addBlock', '+ Add block to this time')} {segment.time !== time ? `(${segment.time})` : ''}
                            </button>
                          );
                        }
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Schedule Picker Modal Overlay */}
      {(selectedSlotTime || editingAnchor) && createPortal(
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200"
          onClick={() => {
            setSelectedSlotTime(null);
            setEditingAnchor(null);
          }}
        >
          <div 
            id="schedule-picker-modal"
            className="bg-card border border-border rounded-xl p-6 max-w-md w-full shadow-lg flex flex-col max-h-[80vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-lg font-sans font-medium text-foreground pr-8">
                {editingAnchor 
                  ? t('calendar.editSchedule', { title: editingAnchor.item_details?.title, defaultValue: 'Edit Schedule: {{title}}' }) 
                  : t('calendar.scheduleTitle', { title: selectedSlotTime, defaultValue: 'Schedule: {{title}}' })}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSelectedSlotTime(null);
                  setEditingAnchor(null);
                }}
                className="absolute top-4 right-4 rounded-full h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Start Time & Duration Inputs */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-sans text-muted-foreground uppercase mb-1">
                  {t('calendar.startTime', 'START TIME')}
                </label>
                <div className="flex gap-2 items-center">
                  <select
                    value={hourVal}
                    onChange={(e) => setHourVal(e.target.value)}
                    className="flex-1 bg-background border border-border/50 rounded-lg p-2 text-sm font-sans text-foreground focus:outline-none"
                  >
                    {Array.from({ length: 16 }, (_, i) => {
                      const hrStr = String(7 + i).padStart(2, '0');
                      return <option key={hrStr} value={hrStr}>{hrStr}</option>;
                    })}
                  </select>
                  <span className="text-muted-foreground font-bold font-sans text-sm">:</span>
                  <select
                    value={minVal}
                    onChange={(e) => setMinVal(e.target.value)}
                    className="flex-1 bg-background border border-border/50 rounded-lg p-2 text-sm font-sans text-foreground focus:outline-none"
                  >
                    {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(min => (
                      <option key={min} value={min}>{min}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-sans text-muted-foreground uppercase mb-1">
                  {t('calendar.duration', 'DURATION')}
                </label>
                <select
                  value={[30, 60, 90, 120].includes(Number(duration)) ? duration : 'custom'}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDuration(val === 'custom' ? 'custom' : Number(val));
                    if (val === 'custom') {
                      setCustomDurationVal(2);
                      setDurationUnit('hours');
                    }
                  }}
                  className="w-full bg-background border border-border/50 rounded-lg p-2 text-sm font-sans text-foreground focus:outline-none"
                >
                  <option value={30}>30 {t('calendar.mins', 'Mins').toLowerCase()}</option>
                  <option value={60}>60 {t('calendar.mins', 'Mins').toLowerCase()} (1 {t('calendar.hours', 'Hour').toLowerCase()})</option>
                  <option value={90}>90 {t('calendar.mins', 'Mins').toLowerCase()}</option>
                  <option value={120}>120 {t('calendar.mins', 'Mins').toLowerCase()} (2 {t('calendar.hours', 'Hours').toLowerCase()})</option>
                  <option value="custom">{t('calendar.custom', 'Custom...')}</option>
                </select>
              </div>
            </div>

            {/* Custom Duration Input Section */}
            {duration === 'custom' && (
              <div className="mb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                <label className="block text-xs font-sans text-muted-foreground uppercase mb-1">
                  {t('calendar.duration', 'DURATION')}
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    min="1"
                    max="1440"
                    value={customDurationVal}
                    onChange={(e) => setCustomDurationVal(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="flex-1 bg-background border border-border/50 rounded-lg p-2 text-sm font-sans text-foreground focus:outline-none"
                  />
                  <select
                    value={durationUnit}
                    onChange={(e) => setDurationUnit(e.target.value)}
                    className="bg-background border border-border/50 rounded-lg p-2 text-sm font-sans text-foreground focus:outline-none"
                  >
                    <option value="mins">{t('calendar.mins', 'Mins')}</option>
                    <option value="hours">{t('calendar.hours', 'Hours')}</option>
                  </select>
                </div>
              </div>
            )}

            {editingAnchor ? (
              <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                <div className="bg-muted/30 border border-border/40 rounded-xl p-4 space-y-2">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">{t('calendar.itemType', 'ITEM TYPE')}</div>
                  <div className="text-xs font-semibold uppercase text-foreground">{t(`analytics.${editingAnchor.item_type}.label`, editingAnchor.item_type)}</div>
                  {editingAnchor.item_details?.description && (
                    <>
                      <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground pt-2 border-t border-border/30">{t('calendar.description', 'DESCRIPTION')}</div>
                      <div className="text-xs text-muted-foreground italic leading-relaxed">{editingAnchor.item_details.description}</div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-6 pr-1">
                {/* Beings Selection */}
                {unscheduledBeings.length > 0 && (
                  <div>
                    <h4 className="text-xs font-sans font-semibold text-muted-foreground uppercase mb-2">
                      {t('analytics.being.label', 'Beings')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {unscheduledBeings.map(b => (
                        <button
                          key={b.id}
                          onClick={() => handleSchedule('being', b.id)}
                          className="text-left w-full text-sm font-sans bg-secondary/30 hover:bg-primary/10 rounded-lg py-3 px-4 transition-colors border border-border/30"
                        >
                          {b.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Wants Selection */}
                {unscheduledWants.length > 0 && (
                  <div>
                    <h4 className="text-xs font-sans font-semibold text-muted-foreground uppercase mb-2">
                      {t('analytics.want.label', 'Wants')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {unscheduledWants.map(w => (
                        <button
                          key={w.id}
                          onClick={() => handleSchedule('want', w.id)}
                          className="text-left w-full text-sm font-sans bg-secondary/30 hover:bg-primary/10 rounded-lg py-3 px-4 transition-colors border border-border/30"
                        >
                          {w.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Todos Selection */}
                {unscheduledTodos.length > 0 && (
                  <div>
                    <h4 className="text-xs font-sans font-semibold text-muted-foreground uppercase mb-2">
                      {t('analytics.todo.label', 'Todos')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {unscheduledTodos.map(t => (
                        <button
                          key={t.id}
                          onClick={() => handleSchedule('todo', t.id)}
                          className="text-left w-full text-sm font-sans bg-secondary/30 hover:bg-primary/10 rounded-lg py-3 px-4 transition-colors border border-border/30"
                        >
                          {t.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {unscheduledBeings.length === 0 && unscheduledWants.length === 0 && unscheduledTodos.length === 0 && (
                  <p className="text-sm font-sans text-muted-foreground italic text-center py-4">
                    {t('calendar.noneUnscheduled', 'None unscheduled')}
                  </p>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3 border-t border-border/50 pt-4">
              {editingAnchor && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleUnschedule(editingAnchor.id);
                    setEditingAnchor(null);
                  }}
                  className="rounded-full mr-auto"
                >
                  {t('calendar.unschedule', 'Unschedule')}
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedSlotTime(null);
                  setEditingAnchor(null);
                }}
                className="rounded-full"
              >
                {t('calendar.cancel', 'Cancel')}
              </Button>
              {editingAnchor && (
                <Button
                  onClick={handleUpdateSchedule}
                  className="rounded-full px-6"
                >
                  {t('calendar.update', 'Update')}
                </Button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
